import { cn, Input, Kbd, Skeleton } from '@heroui/react';
import { Search, Xmark } from 'iconoir-react';
import { useState, useEffect, useRef } from 'react';

import { useContentHeaderContext } from './ContentHeaderContext';

import { t } from '~/common/helpers/i18n';

const LABELED_INPUT_STYLES = {
  inputWrapper:
    'hover:!border-gray-400 focus-within:!border-primary py-3.5 px-3 h-[68px]',
  label: '!text-label-md !h-4 !transform-none top-[14px] left-3'
};

const DEFAULT_INPUT_STYLES = {
  inputWrapper: 'bg-surface px-2',
  label: 'z-0'
};

export type ContentHeaderInputProps = React.ComponentPropsWithoutRef<
  typeof Input
> & {
  variant?: 'default' | 'labeled';
  className?: string;
  placeholder?: string;
  label?: string;
  value?: string;
  onChange?: (value: string) => void;
  onKeyDown?: (e: React.KeyboardEvent) => void;
  searchCallback?: (searchText: string) => void;
  name?: string;
  'data-testid'?: string;
};

export function ContentHeaderInput(props: ContentHeaderInputProps) {
  const {
    variant = 'default',
    className,
    placeholder,
    label,
    value: controlledValue,
    onChange: controlledOnChange,
    onKeyDown: controlledOnKeyDown,
    name,
    'data-testid': dataTestId
  } = props;

  const isLabeled = variant === 'labeled';
  const isControlled = controlledValue !== undefined;

  const { searchText, onInputChange, searchCallback, activeOptions, isLoaded } =
    useContentHeaderContext('ContentHeaderInput');

  const inputRef = useRef<HTMLInputElement>(null);
  const [searchValue, setSearchValue] = useState(
    isControlled ? controlledValue : searchText
  );
  const currentValue = isControlled ? controlledValue : searchValue;

  const applyChange = () => {
    if (!isControlled) {
      onInputChange?.(searchValue);
      searchCallback?.({ ...activeOptions, searchText: searchValue });
    }
  };

  const handleChange = (newValue: string) => {
    if (isControlled) {
      controlledOnChange?.(newValue);
    } else {
      setSearchValue(newValue);
    }
  };

  const handleClear = () => {
    if (isControlled) {
      controlledOnChange?.('');
      return;
    }
    setSearchValue('');
    onInputChange?.('');
    searchCallback?.({ ...activeOptions, searchText: '' });
  };

  const renderEndContent = () => {
    if (isLabeled) return undefined;

    if (currentValue) {
      return (
        <button
          type='button'
          onClick={handleClear}
          aria-label={t('label.clear')}
          className='text-on-surface-variant hover:text-on-surface'
        >
          <Xmark className='icon-smallest' />
        </button>
      );
    }

    return (
      <Kbd
        className='bg-surface-dim text-on-surface-variant'
        keys={['command']}
      >
        K
      </Kbd>
    );
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (controlledOnKeyDown) {
      controlledOnKeyDown(e);
    } else if (e.key === 'Enter') {
      applyChange();
    }
  };

  useEffect(() => {
    if (!isControlled) {
      setSearchValue(searchText);
    }
  }, [searchText, isControlled]);

  useEffect(() => {
    if (isControlled && controlledValue !== undefined) {
      setSearchValue(controlledValue);
    }
  }, [controlledValue, isControlled]);

  useEffect(() => {
    if (isLabeled) return;

    const handleShortcutKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
      }
    };

    document.addEventListener('keydown', handleShortcutKeyDown);
    // eslint-disable-next-line consistent-return
    return () => {
      document.removeEventListener('keydown', handleShortcutKeyDown);
    };
  }, [isLabeled]);

  if (!isLoaded && !isControlled) {
    return (
      <Skeleton
        className={cn(
          'rounded-md',
          isLabeled ? 'h-[68px] w-full' : 'h-[42px] w-full max-w-[516px]'
        )}
      />
    );
  }

  return (
    <Input
      label={isLabeled ? label : undefined}
      placeholder={placeholder ?? (isLabeled ? undefined : 'Search')}
      type='text'
      name={name || 'search'}
      variant={isLabeled ? 'bordered' : undefined}
      classNames={{
        ...(isLabeled ? LABELED_INPUT_STYLES : DEFAULT_INPUT_STYLES),
        inputWrapper: cn(
          'border-1 border-solid border-outline rounded-md',
          isLabeled
            ? LABELED_INPUT_STYLES.inputWrapper
            : DEFAULT_INPUT_STYLES.inputWrapper
        ),
        input: 'text-body-regular-md',
        base: cn(isLabeled ? undefined : 'max-w-[516px]', className)
      }}
      startContent={
        isLabeled ? undefined : (
          <Search className='icon-smallest text-on-surface-variant' />
        )
      }
      endContent={renderEndContent()}
      ref={inputRef}
      value={currentValue}
      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
        handleChange(e.target.value)
      }
      onKeyDown={handleKeyDown}
      data-testid={dataTestId}
    />
  );
}
